import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { User } from '~/users/types';
import type { Location } from '~/tracking/types';
import type { CustomChartData } from '~/common/types';
import { LocationTypeEnum, LocationSourceEnum } from '~/tracking/types';
import moment from 'moment';
import { ChartDataset } from 'chart.js';
import {
  colors,
  getLast7DaysArray,
  getMonthsArray,
  defaultColors,
} from '~/common/components/Charts/customChartjs';
import { TrackingsRequest } from '~/tracking/api/requests';
import { UsersRequest } from '~/users/api/requests';
import { convertFilter } from '~/common/modules/dateTimeUtils';

export class LoginDashboardService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async logins(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const userSelectedDate = params.filter?.date;
    params.filter = convertFilter(params.filter);

    const request = new TrackingsRequest({
      token: accessToken,
      params: { ...params },
    });

    const response = await this.apiClient.request(request);
    return this.transformLogins(response?.data, userSelectedDate);
  }

  async usersLogins(params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new UsersRequest({
      token: accessToken,
      params: { ...params },
    });

    const response = await this.apiClient.request(request);
    return this.transformUsersLogins(response?.data);
  }

  transformLogins(logins: Location[], date: Record<string, string>) {
    if (!logins.length) {
      return {
        monthlyLogins: [],
        dailyLogins: [],
        groupLogins: [],
      };
    }

    const last7Days = getLast7DaysArray(date);
    const lastMonths = getMonthsArray(date);

    const filteredLogins = logins.filter(
      (login) =>
        login.type === LocationTypeEnum.LOGIN &&
        login.user?.data?.groups &&
        login.user.data.groups.data.length > 0,
    );

    const monthlyLogins = this.transformMonthlyLogins(
      filteredLogins,
      lastMonths,
    );

    const groupLogins = this.transformGroupLogins(filteredLogins);

    const filteredDailyLogins = logins.filter((login) =>
      last7Days.includes(moment(login.created_at).format('YYYY/MM/DD')),
    );

    const dailyLogins = this.transformDailyLogins(
      filteredDailyLogins,
      last7Days,
    );

    return {
      monthlyLogins: monthlyLogins,
      dailyLogins: dailyLogins,
      groupLogins: groupLogins,
    };
  }

  transformMonthlyLogins(logins: Location[], months: string[]) {
    const groupData = {};
    logins.forEach((login) => {
      const loginDate = moment(login.created_at);
      const yearMonth = loginDate.format('YYYY/MM');

      login.user?.data?.groups?.data.forEach((group) => {
        if (!groupData[group.name]) {
          groupData[group.name] = {
            label: group.name,
            backgroundColor:
              defaultColors[
                Object.keys(groupData).length % defaultColors.length
              ],
            data: months.map((month) => ({
              x: moment(month, 'YYYY/MM').format('MMM/YYYY'),
              y: 0,
              meta: [],
            })),
          };
        }

        const monthIndex = months.indexOf(yearMonth);
        if (monthIndex !== -1) {
          const userName = login.user?.data?.fullName;
          const userMeta = groupData[group.name].data[monthIndex].meta.find(
            (meta: { name: string; total: number }) => meta.name === userName,
          );

          if (userMeta) {
            userMeta.total += 1;
          } else {
            groupData[group.name].data[monthIndex].meta.push({
              name: userName,
              total: 1,
            });
          }

          groupData[group.name].data[monthIndex].y += 1;
        }
      });
    });

    const datasets: ChartDataset<'bar', CustomChartData[]>[] =
      Object.values(groupData);

    return datasets;
  }

  transformDailyLogins(logins: Location[], days: string[]) {
    const missedCheckIn: ChartDataset<'bar', CustomChartData[]> = {
      type: 'bar',
      label: 'Missed Check-In',
      data: [] as CustomChartData[],
      backgroundColor: colors.yellow,
      order: 1,
    };
    const loneWorker: ChartDataset<'line', CustomChartData[]> = {
      type: 'line',
      label: 'Lone Worker',
      data: [] as CustomChartData[],
      backgroundColor: colors.teal,
      borderColor: colors.teal,
      tension: 0.4,
    };
    const journey: ChartDataset<'line', CustomChartData[]> = {
      type: 'line',
      label: 'Journey Management',
      data: [] as CustomChartData[],
      backgroundColor: colors.purple,
      borderColor: colors.purple,
      tension: 0.4,
    };

    days.forEach((date) => {
      missedCheckIn.data.push({ x: date, y: 0, meta: [] });
      loneWorker.data.push({ x: date, y: 0, meta: [] });
      journey.data.push({ x: date, y: 0, meta: [] });
    });

    const updateDatasetEntry = (
      dataset: ChartDataset<'bar' | 'line', CustomChartData[]>,
      date: string,
      itemName: string,
    ) => {
      const dayData = dataset.data.find((d) => d?.x === date);
      if (dayData) {
        const existingMeta = dayData.meta.find((m) => m.name === itemName);

        if (typeof dayData.y === 'string') {
          dayData.y = parseInt(dayData.y, 10) + 1;
        } else {
          dayData.y += 1;
        }
        if (existingMeta) {
          (existingMeta.total as number) += 1;
        } else {
          dayData.meta.push({ name: itemName, total: 1 });
        }
      }
    };

    logins.forEach((login) => {
      const date = moment(login.created_at).format('YYYY/MM/DD');

      if (days.includes(date)) {
        const userName = login.user?.data.fullName ?? '';

        if (login.type === LocationTypeEnum.MISSED_CHECK_IN) {
          updateDatasetEntry(missedCheckIn, date, userName);
        }

        if (
          login.source === LocationSourceEnum.LONE_WORKER &&
          login.type === LocationTypeEnum.LOGIN
        ) {
          updateDatasetEntry(loneWorker, date, userName);
        }

        if (
          login.source === LocationSourceEnum.JOURNEY &&
          login.type === LocationTypeEnum.LOGIN
        ) {
          updateDatasetEntry(journey, date, userName);
        }
      }
    });

    const dailyLogins: ChartDataset<'bar' | 'line', CustomChartData[]>[] = [
      missedCheckIn,
      loneWorker,
      journey,
    ];

    return dailyLogins;
  }

  transformGroupLogins(logins: Location[]) {
    const groupSourceData = {};

    logins.forEach((login) => {
      login.user?.data.groups?.data.forEach((group) => {
        if (!group.allUsers) {
          const groupName = group.name;
          const source = login.source;
          const userFullName = login.user?.data.fullName;
          if (!groupSourceData[groupName]) {
            groupSourceData[groupName] = {};
          }

          if (!groupSourceData[groupName][source]) {
            groupSourceData[groupName][source] = { total: 0, meta: {} };
          }
          groupSourceData[groupName][source].total += 1;

          if (!groupSourceData[groupName][source].meta[userFullName]) {
            groupSourceData[groupName][source].meta[userFullName] = 0;
          }
          groupSourceData[groupName][source].meta[userFullName] += 1;
        }
      });
    });

    const datasets: ChartDataset<'bar', CustomChartData[]>[] = [];
    const datasetColors = [colors.teal, colors.purple];

    const sources = [...new Set(logins.map((login) => login.source))];
    sources.forEach((source, sourceIndex) => {
      const data: CustomChartData[] = [];

      Object.keys(groupSourceData).forEach((groupName) => {
        const sourceData = groupSourceData[groupName][source];
        const count = sourceData ? sourceData.total : 0;
        const metaArray = sourceData
          ? Object.entries(sourceData.meta).map(([name, total]) => ({
              name,
              total: Number(total),
            }))
          : [];

        data.push({
          x: groupName,
          y: count,
          meta: metaArray,
        });
      });

      datasets.push({
        label:
          source === LocationSourceEnum.LONE_WORKER
            ? 'Lone Worker'
            : 'Journey Management',
        backgroundColor: datasetColors[sourceIndex],
        data,
      });
    });

    return datasets;
  }

  formatLocationTime(time: string): number {
    if (typeof time !== 'string') {
      throw new Error('Invalid time format. Expected a string.');
    }

    const parts = time.split(':').map(Number);

    if (parts.some(isNaN)) {
      throw new Error('Invalid time values. Expected numeric values.');
    }

    const [hours = 0, minutes = 0] = parts;

    return hours * 3600 + minutes * 60;
  }

  transformUsersLogins(users: User[]) {
    const activeUsers: { label: string }[] = [];
    const inactiveUsers: { label: string }[] = [];
    let totalHours: number = 0;

    users.forEach((user: User) => {
      if (user.locations?.data.length) {
        activeUsers.push({ label: user.fullName });
        user.locations.data.forEach((locations) => {
          if (locations.type !== LocationTypeEnum.LOCATION_UPDATE) {
            totalHours += this.formatLocationTime(locations.time);
          }
        });
      } else {
        inactiveUsers.push({ label: user.fullName });
      }
    });

    return {
      usersTotal: users.length,
      activeUsers: activeUsers,
      inactiveUsers: inactiveUsers,
      totalHours: (totalHours / 3600).toFixed(0),
    };
  }
}
